import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Title,
  Text,
  Card,
  Stack,
  Center,
  Grid,
  ThemeIcon,
  Button,
  Badge,
  Alert,
  Code
} from '@mantine/core';
import { IconFileReport, IconCalculator, IconApi, IconAlertCircle, IconBrandTwilio, IconArrowLeft, IconHeadset} from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import { usePlatform } from '../../shared/contexts/PlatformContext';
import { setTokenProvider } from './services/api';
import SaToolsApp from './SaToolsApp';
import AiCalculatorApp from './AiCalculatorApp';
import DemoApiViewerApp from './DemoApiViewerApp';
import TwilioCalculatorApp from './TwilioCalculatorApp';
import EccoCalculatorApp from './EccoCalculatorApp';
import PlatformBackButton from '../../components/PlatformBackButton';

const tools = {
  ipa: {
    name: 'IPA Report Generator',
    description: 'Comprehensive Interaction Process Automation assessment and professional report generation with AI-powered insights',
    color: 'purple',
    icon: IconFileReport,
    component: SaToolsApp,
    comingSoon: false
  },
  calculator: {
    name: 'AI Calculator',
    description: 'Estimate AI agent licensing costs across 11 solutions and 7 billing models with multi-scenario support',
    color: 'violet',
    icon: IconCalculator,
    component: AiCalculatorApp,
    comingSoon: false
  },
  demoApi: {
    name: 'Presidio Demo API Viewer',
    description: 'Manage banking AI agent demo accounts — get, add, and update users and transactions via the Presidio API',
    color: 'teal',
    icon: IconApi,
    component: DemoApiViewerApp,
    comingSoon: false
  },
  ecco: {
    name: 'ECCO MS Pricing Calculator',
    description: 'ECCO Managed Services pricing for Contact Center — OEF hour-based formulas, Vigilus RFQ generator, and SKU quote summary',
    color: 'blue',
    icon: IconHeadset,
    component: EccoCalculatorApp,
    comingSoon: false
  },
  twilioQuote: {
    name: 'Twilio Quote Builder',
    description: 'Build Twilio SaaS quotes — search 207 products, set volumes and discounts, generate a MyOrders summary with internal margin view',
    color: 'red',
    icon: IconBrandTwilio,
    component: TwilioCalculatorApp,
    comingSoon: false
  }
};

function SaToolsHub({ onBackToPlatformSelector, platformInfo }) {
  const [selectedTool, setSelectedTool] = useState(null);
  const [cardHeight, setCardHeight] = useState(null);
  const cardRefs = useRef({});
  const { getApiToken } = useAuth();
  const { selectPlatform } = usePlatform();

  useEffect(() => {
    setTokenProvider(getApiToken);
  }, [getApiToken]);

  useEffect(() => {
    selectPlatform('satools', platformInfo);
  }, [selectPlatform, platformInfo]);

  useEffect(() => {
    const heights = Object.values(cardRefs.current).filter(Boolean).map(el => el.offsetHeight);
    if (heights.length) setCardHeight(Math.max(...heights));
  }, []);

  if (selectedTool) {
    const tool = tools[selectedTool];
    const ToolComponent = tool.component;
    return (
      <ToolComponent
        onBackToHome={() => setSelectedTool(null)}
        onBackToPlatformSelector={onBackToPlatformSelector}
        platformInfo={platformInfo}
      />
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
        padding: '40px 20px'
      }}
    >
      <Container size="lg">
        {/* Back to Platform Selector */}
        <PlatformBackButton onClick={onBackToPlatformSelector} onGradient mb="lg" />

        {/* Env-var sanity alert — appears when SA Tools backend URL isn't wired up so
            users aren't surprised by silent failures on the first AI call. */}
        {!process.env.REACT_APP_SATOOLS_API_BASE_URL && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="filled"
            mb="lg"
          >
            <Text size="sm">
              SA Tools API URL is not configured. Set
              {' '}<Code>REACT_APP_SATOOLS_API_BASE_URL</Code>{' '}
              in your environment to enable AI-powered ratings, rewriting, and report generation.
            </Text>
          </Alert>
        )}

        <Center>
          <Stack align="center" gap="xl" style={{ maxWidth: '800px', width: '100%' }}>
            {/* Header */}
            <Stack align="center" gap="md">
              <Title
                order={1}
                size="3rem"
                fw={700}
                style={{ color: 'white', textAlign: 'center' }}
              >
                SA Tools
              </Title>
              <Text
                size="xl"
                style={{ color: 'rgba(255,255,255,0.9)', textAlign: 'center' }}
              >
                Select a tool to get started
              </Text>
            </Stack>

            {/* Tool Cards */}
            <Grid gutter="xl" style={{ width: '100%' }}>
              {Object.entries(tools).map(([key, tool]) => {
                const IconComponent = tool.icon;

                return (
                  <Grid.Col key={key} span={{ base: 12, sm: 6, md: 4 }}>
                    <Card
                      ref={el => { cardRefs.current[key] = el; }}
                      shadow="xl"
                      padding="xl"
                      radius="md"
                      onClick={() => setSelectedTool(key)}
                      style={{
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: cardHeight ?? undefined,
                        minHeight: '300px',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '2px solid transparent',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.2)';
                        e.currentTarget.style.border = `2px solid var(--mantine-color-${tool.color}-6)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                        e.currentTarget.style.border = '2px solid transparent';
                      }}
                    >
                      <Stack align="center" gap="md" style={{ flex: 1, justifyContent: 'space-between', padding: '10px 0' }}>
                        <Stack align="center" gap="md">
                          <ThemeIcon size={70} radius="md" color={tool.color} variant="light">
                            <IconComponent size={40} />
                          </ThemeIcon>
                          <Stack align="center" gap={4}>
                            <Title order={3} size="1.2rem" fw={600} ta="center">
                              {tool.name}
                            </Title>
                            {tool.comingSoon && (
                              <Badge color="gray" variant="light" size="sm">Coming Soon</Badge>
                            )}
                          </Stack>
                        </Stack>

                        <Text
                          size="sm"
                          c="dimmed"
                          ta="center"
                          style={{ lineHeight: 1.4, flex: 1, display: 'flex', alignItems: 'center', padding: '0 10px' }}
                        >
                          {tool.description}
                        </Text>

                        <Text
                          size="xs"
                          c={tool.color}
                          ta="center"
                          fw={600}
                          style={{ textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8 }}
                        >
                          Click to select
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                );
              })}
            </Grid>

            {/* Footer */}
            <Stack align="center" gap="xs" style={{ marginTop: '20px' }}>
              <Text size="xs" style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                Presidio Networked Solutions, Inc.
              </Text>
            </Stack>
          </Stack>
        </Center>
      </Container>
    </Box>
  );
}

export default SaToolsHub;
